/**
 * useAnalytics — current period (every section) and, when comparing, the previous
 * period (fetched in parallel via `shift: 'previous'`, so comparison never doubles the
 * wait). Results are cached for five minutes by filter set, so moving between pages and
 * pressing Back is instant.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { functions } from '../firebase';
import { AnalyticsRepository } from '../models/AnalyticsRepository';

const cache = new Map();
const TTL = 5 * 60 * 1000;

export function useAnalytics(filters) {
  const repo = useMemo(() => new AnalyticsRepository(functions), []);
  const { range, from, to, environment, peopleOnly, includeInternal, compare } = filters;
  const base = useMemo(
    () => ({ range, from, to, environment, peopleOnly, includeInternal }),
    [range, from, to, environment, peopleOnly, includeInternal],
  );
  const key = JSON.stringify(base);
  const cached = cache.get(key);
  const fresh = cached && Date.now() - cached.at < TTL ? cached : null;

  const [data, setData] = useState(fresh ? fresh.data : null);
  const [previous, setPrevious] = useState(fresh ? fresh.previous : null);
  const [loading, setLoading] = useState(!fresh);
  const [error, setError] = useState(null);
  const stamp = useRef(0);

  const load = useCallback(async (force = false) => {
    const hit = cache.get(key);
    if (!force && hit && Date.now() - hit.at < TTL && (!compare || hit.previous)) {
      setData(hit.data); setPrevious(hit.previous); setLoading(false); setError(null);
      return;
    }
    const me = ++stamp.current;
    setLoading(true);
    setError(null);
    try {
      const [cur, prev] = await Promise.all([
        repo.get(base),
        compare ? repo.get({ ...base, shift: 'previous', sections: ['overview'] }).catch(() => null) : null,
      ]);
      if (me !== stamp.current) return; // a newer request superseded this one
      const previousOverview = prev ? prev.overview : null;
      cache.set(key, { at: Date.now(), data: cur, previous: previousOverview });
      setData(cur);
      setPrevious(previousOverview);
    } catch (e) {
      if (me !== stamp.current) return;
      setError(e.message || 'Could not load analytics');
    } finally {
      if (me === stamp.current) setLoading(false);
    }
  }, [key, base, compare, repo]);

  useEffect(() => { load(false); }, [load]);

  return { data, previous: compare ? previous : null, loading, error, refresh: () => load(true) };
}

export default useAnalytics;
